import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Trophy, Target, Star, Flame, BookOpen, ArrowRight,
  Award, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = sessionStorage.getItem("quiz_session_id");
  if (!sid) { sid = Math.random().toString(36).slice(2); sessionStorage.setItem("quiz_session_id", sid); }
  return sid;
};

export function ProgressWidget() {
  const { user, token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const sid = getSessionId();
      const qs = user ? "" : `?session_id=${sid}`;
      try {
        const [sumRes, badgeRes] = await Promise.all([
          axios.get(`${API}/quiz-progress/summary${qs}`, { headers }),
          axios.get(`${API}/quiz-progress/badges${qs}`, { headers }),
        ]);
        setSummary(sumRes.data);
        setBadges(badgeRes.data);
      } catch { }
      finally { setLoading(false); }
    };
    fetchData();
  }, [user, token]);

  if (loading) return null;
  if (!summary || summary.total_quizzes === 0) {
    return (
      <Card className="rounded-2xl border-0 shadow-soft overflow-hidden" data-testid="progress-widget-empty">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0482d]/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-[#e0482d]" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Quiz Progress</h3>
              <p className="text-xs text-slate-500">Take a quiz to start tracking</p>
            </div>
          </div>
          <Button asChild size="sm" className="w-full bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl text-xs h-9">
            <Link to="/sample-preview" data-testid="widget-start-quiz">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Take a Sample Quiz <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="rounded-2xl border-0 shadow-soft overflow-hidden" data-testid="progress-widget">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-bold">Quiz Progress</span>
          </div>
          <Link to="/my-progress" className="text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1" data-testid="view-full-progress">
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-3 mb-3">
            {[
              { icon: Target, value: summary.total_quizzes, label: "Quizzes", color: "text-blue-500" },
              { icon: Trophy, value: `${summary.avg_score}%`, label: "Avg Score", color: "text-emerald-500" },
              { icon: Star, value: `${summary.best_score}%`, label: "Best", color: "text-amber-500" },
              { icon: Flame, value: `${summary.current_streak}d`, label: "Streak", color: "text-red-500" },
            ].map((s, i) => (
              <div key={i} className="text-center" data-testid={`widget-stat-${i}`}>
                <s.icon className={`w-4 h-4 mx-auto mb-0.5 ${s.color}`} />
                <p className="text-sm font-bold text-slate-900">{s.value}</p>
                <p className="text-[10px] text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
          {badges && badges.earned_count > 0 && (
            <div className="flex items-center gap-1.5 mb-3">
              <Award className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs text-slate-600 font-medium">{badges.earned_count} badge{badges.earned_count !== 1 ? "s" : ""} earned</span>
              <div className="flex items-center -space-x-1 ml-auto">
                {badges.badges.filter(b => b.earned).slice(0, 4).map(b => (
                  <div key={b.key} className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center"
                    style={{ backgroundColor: `${b.color}20` }} title={b.name}>
                    <span style={{ color: b.color, fontSize: "10px" }}>
                      {b.icon === "rocket" ? "R" : b.icon === "zap" ? "Z" : b.icon === "star" ? "S" : b.icon === "crown" ? "C" : "B"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline" className="flex-1 rounded-xl text-xs h-8">
              <Link to="/my-progress"><Trophy className="w-3 h-3 mr-1" /> Details</Link>
            </Button>
            <Button asChild size="sm" className="flex-1 bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl text-xs h-8">
              <Link to="/sample-preview"><BookOpen className="w-3 h-3 mr-1" /> Quiz</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
